<script setup>
// Aba Disparos da caixa WhatsApp Cloud (so id 1): seleciona um template
// Aprovado, mapeia as variaveis {{n}} para colunas do CSV (telefone/cpf/nome
// obrigatorias + livres) ou valores digitados, dispara com pausa automatica
// (10 erros iguais seguidos) e acompanha o relatorio por campanha com status
// vivo Disparado/Entregue/Lido/Erro vindo do webhook da Meta.
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxTemplatesAPI from 'dashboard/api/inboxTemplates';
import InboxDispatchesAPI from 'dashboard/api/inboxDispatches';
import DispatchItemsReport from '../components/DispatchItemsReport.vue';

const props = defineProps({
  inbox: {
    type: Object,
    default: null,
  },
  // Modo MULTI-CAIXA (menu Meta -> Disparo): lista de caixas selecionadas.
  // A lista de clientes é UMA só, dividida em rodízio entre as caixas; o
  // template é casado pelo NOME (caixa sem o template fica de fora).
  inboxes: {
    type: Array,
    default: null,
  },
});

const REQUIRED_COLUMNS = ['telefone', 'cpf', 'nome'];
const ITEM_BATCH_SIZE = 1000;

const isMulti = computed(() => Array.isArray(props.inboxes));
const targets = computed(() =>
  (isMulti.value ? props.inboxes : [props.inbox]).filter(Boolean)
);

// --- templates aprovados --------------------------------------------------
const templates = ref([]);
const templatesByInbox = ref({});
const isLoadingTemplates = ref(false);
const selectedIdx = ref('');
// multi: chave name|language do template escolhido na UNIÃO das caixas
const selectedKey = ref('');

const approvedTemplates = computed(() =>
  templates.value.filter(
    t => t.status === 'APPROVED' && t.category !== 'AUTHENTICATION'
  )
);

const fetchTemplates = async () => {
  isLoadingTemplates.value = true;
  try {
    const results = await Promise.all(
      targets.value.map(async box => {
        try {
          const { data } = await InboxTemplatesAPI.listTemplates(box.id);
          return [box.id, data.data || []];
        } catch (e) {
          return [box.id, []];
        }
      })
    );
    templatesByInbox.value = Object.fromEntries(results);
    templates.value = templatesByInbox.value[targets.value[0]?.id] || [];
  } finally {
    isLoadingTemplates.value = false;
  }
};

// União dos templates Aprovados das caixas selecionadas, agrupados por
// nome+idioma — cada entrada sabe QUAIS caixas têm o template (boxIds).
const unionTemplates = computed(() => {
  const seen = new Map();
  targets.value.forEach(box => {
    (templatesByInbox.value[box.id] || [])
      .filter(t => t.status === 'APPROVED' && t.category !== 'AUTHENTICATION')
      .forEach(t => {
        const key = `${t.name}|${t.language}`;
        if (!seen.has(key)) seen.set(key, { key, template: t, boxIds: [] });
        seen.get(key).boxIds.push(box.id);
      });
  });
  return [...seen.values()].sort((a, b) =>
    a.template.name.localeCompare(b.template.name)
  );
});

const selectedUnion = computed(
  () => unionTemplates.value.find(u => u.key === selectedKey.value) || null
);

// Caixas que efetivamente disparam: no multi, só as selecionadas QUE TÊM o
// template escolhido (mesmo nome+idioma). As demais ficam de fora, visível
// nos chips abaixo do seletor.
const effectiveBoxes = computed(() => {
  if (!isMulti.value) return targets.value;
  if (!selectedUnion.value) return [];
  return targets.value.filter(b => selectedUnion.value.boxIds.includes(b.id));
});

// selectedIdx '' = nenhum template escolhido. Number('') vale 0, entao sem o
// guard o 1o template era "selecionado" sozinho quando a lista chegava da
// Meta — com o bodyMap ainda vazio (o watch nunca disparou), o render lia
// bodyMap[v].source de undefined e derrubava a aba inteira (tela em branco).
const selectedTemplate = computed(() => {
  if (isMulti.value) return selectedUnion.value?.template || null;
  return selectedIdx.value === ''
    ? null
    : approvedTemplates.value[Number(selectedIdx.value)] || null;
});

// Estrutura do template: variaveis do corpo, cabecalho e botoes com parametro.
const templateStructure = computed(() => {
  const t = selectedTemplate.value;
  if (!t) return null;
  const comps = t.components || [];
  const body = comps.find(c => c.type === 'BODY');
  const header = comps.find(c => c.type === 'HEADER');
  const buttonsComp = comps.find(c => c.type === 'BUTTONS');
  const bodyText = body?.text || '';
  const bodyVars = [
    ...new Set([...bodyText.matchAll(/{{\s*(\w+)\s*}}/g)].map(m => m[1])),
  ];
  const headerVar =
    header?.format === 'TEXT' && /{{\s*1\s*}}/.test(header.text || '');
  const headerMediaType = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(
    header?.format
  )
    ? header.format.toLowerCase()
    : null;
  const buttons = (buttonsComp?.buttons || [])
    .map((b, index) => ({ ...b, index }))
    .filter(
      b =>
        (b.type === 'URL' && /{{\s*1\s*}}/.test(b.url || '')) ||
        b.type === 'COPY_CODE'
    )
    .map(b => ({
      index: b.index,
      sub_type: b.type === 'URL' ? 'url' : 'copy_code',
      label: b.type === 'URL' ? `URL "${b.text}"` : 'Código de cópia',
    }));
  return {
    bodyText,
    bodyVars,
    headerVar,
    headerText: header?.text || '',
    headerMediaType,
    buttons,
  };
});

// --- formulario da campanha -------------------------------------------------
const campaignName = ref('');
const mode = ref('csv');
const bodyMap = ref({});
const headerMap = ref({ source: 'column', value: '' });
const buttonMaps = ref({});
const mediaUrl = ref('');
const isLaunching = ref(false);
const launchError = ref('');

const resetMappings = () => {
  const ts = templateStructure.value;
  bodyMap.value = {};
  buttonMaps.value = {};
  headerMap.value = { source: 'column', value: '' };
  mediaUrl.value = '';
  if (!ts) return;
  ts.bodyVars.forEach(v => {
    bodyMap.value[v] = { source: 'column', value: '' };
  });
  ts.buttons.forEach(b => {
    buttonMaps.value[b.index] = { source: 'column', value: '' };
  });
};

watch(selectedIdx, resetMappings);
watch(selectedKey, resetMappings);
// Seleção de caixas mudou (multi): recarrega templates/campanhas e limpa o
// template escolhido se alguma caixa dele saiu da seleção.
watch(
  () => targets.value.map(b => b.id).join(','),
  () => {
    fetchTemplates();
    fetchCampaigns();
  }
);

// --- intervalo entre envios + modo do tempo (Kairo 10/07/2026) ---------------
// min:seg entre envios. Modo POR NÚMERO = cada caixa tem o próprio relógio
// (todas em paralelo); POR GRUPO = rodízio por mensagem entre as caixas (1
// envio por intervalo no grupo inteiro).
const intervalMin = ref(0);
const intervalSec = ref(0);
const timeMode = ref('numero');

const intervalSeconds = computed(
  () => Number(intervalMin.value || 0) * 60 + Number(intervalSec.value || 0)
);

const intervalLabel = computed(() => {
  const m = Number(intervalMin.value || 0);
  const s = Number(intervalSec.value || 0);
  if (!m && !s) return 'mínimo do sistema';
  const parts = [];
  if (m) parts.push(`${m} min`);
  if (s) parts.push(`${s} seg`);
  return parts.join(' e ');
});

// --- CSV ---------------------------------------------------------------------
const csvHeaders = ref([]);
const csvRows = ref([]);
const csvFileName = ref('');
const csvError = ref('');

const normalizeHeader = h =>
  h
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_');

const parseCsv = text => {
  const clean = text.replace(/^\uFEFF/, '');
  const lines = clean.split(/\r\n|\n|\r/).filter(l => l.trim().length);
  if (!lines.length) return { headers: [], rows: [] };
  const headerLine = lines[0];
  // Detecta o separador pelo cabecalho: TAB (Excel/planilha colada), ; ou ,
  const counts = {
    '\t': (headerLine.match(/\t/g) || []).length,
    ';': (headerLine.match(/;/g) || []).length,
    ',': (headerLine.match(/,/g) || []).length,
  };
  const delimiter = ['\t', ';', ','].reduce(
    (best, cand) => (counts[cand] > counts[best] ? cand : best),
    ';'
  );
  const parseLine = line => {
    const out = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"') {
          if (line[i + 1] === '"') {
            cur += '"';
            i += 1;
          } else {
            inQuotes = false;
          }
        } else {
          cur += ch;
        }
      } else if (ch === '"') {
        inQuotes = true;
      } else if (ch === delimiter) {
        out.push(cur);
        cur = '';
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out.map(v => v.trim());
  };
  const headers = parseLine(headerLine).map(normalizeHeader);
  const rows = lines.slice(1).map((line, idx) => {
    const values = parseLine(line);
    const data = {};
    headers.forEach((h, i) => {
      if (h) data[h] = values[i] ?? '';
    });
    return { lineNo: idx + 2, data };
  });
  return { headers: headers.filter(Boolean), rows };
};

const onCsvSelected = event => {
  const [file] = event.target.files || [];
  if (!file) return;
  csvError.value = '';
  const reader = new FileReader();
  reader.onload = () => {
    // Excel salva CSV em Windows-1252 (PC) ou Mac Roman (Excel do Mac); ler
    // como UTF-8 cru viraria "Voc�"/"prŽ". Tenta UTF-8 estrito; se falhar,
    // decodifica nas duas e fica com a que render mais acentos validos de PT.
    const bytes = new Uint8Array(reader.result || new ArrayBuffer(0));
    let text;
    try {
      text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch {
      const win = new TextDecoder('windows-1252').decode(bytes);
      const mac = new TextDecoder('macintosh').decode(bytes);
      const score = t =>
        (t.match(/[áéíóúâêôãõàçüÁÉÍÓÚÂÊÔÃÕÀÇÜ]/g) || []).length;
      text = score(mac) > score(win) ? mac : win;
    }
    const { headers, rows } = parseCsv(text);
    const missing = REQUIRED_COLUMNS.filter(c => !headers.includes(c));
    if (missing.length) {
      csvHeaders.value = [];
      csvRows.value = [];
      csvFileName.value = '';
      csvError.value = `CSV sem as colunas obrigatórias: ${missing.join(', ')}`;
      return;
    }
    csvHeaders.value = headers;
    csvRows.value = rows;
    csvFileName.value = file.name;
  };
  reader.readAsArrayBuffer(file);
  event.target.value = '';
};

const downloadCsvModel = () => {
  const ts = templateStructure.value;
  const extras = ts ? ts.bodyVars.map(v => `var_${v}`) : [];
  const header = [...REQUIRED_COLUMNS, ...extras].join(';');
  const example = ['5521999999999', '12345678901', 'Maria da Silva']
    .concat(extras.map(() => 'exemplo'))
    .join(';');
  const blob = new Blob([`${header}\n${example}\n`], {
    type: 'text/csv;charset=utf-8;',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'modelo_disparo.csv';
  link.click();
  URL.revokeObjectURL(link.href);
};

// --- modo digitado ---------------------------------------------------------
const manualText = ref('');

const manualRows = computed(() =>
  manualText.value
    .split(/\r\n|\n|\r/)
    .map(l => l.trim())
    .filter(Boolean)
    .map((line, idx) => {
      const parts = line.split(/[;,]/).map(p => p.trim());
      return {
        lineNo: idx + 1,
        data: {
          telefone: parts[0] || '',
          cpf: parts[1] || '',
          nome: parts.slice(2).join(' ') || '',
        },
      };
    })
);

// --- validacao das linhas ----------------------------------------------------
const normalizePhone = raw => {
  let d = String(raw || '').replace(/\D/g, '');
  d = d.replace(/^0+/, '');
  if (d.length === 10 || d.length === 11) d = `55${d}`;
  if (d.length < 12 || d.length > 13 || !d.startsWith('55')) return null;
  return `+${d}`;
};

const normalizeCpf = raw => {
  const d = String(raw || '').replace(/\D/g, '');
  if (d.length < 9 || d.length > 11) return null;
  return d.padStart(11, '0');
};

const mappedColumns = computed(() => {
  if (mode.value === 'manual') return [];
  const ts = templateStructure.value;
  if (!ts) return [];
  const cols = [];
  ts.bodyVars.forEach(v => {
    const spec = bodyMap.value[v];
    if (spec?.source === 'column' && spec.value) cols.push(spec.value);
  });
  if (ts.headerVar && headerMap.value.source === 'column' && headerMap.value.value) {
    cols.push(headerMap.value.value);
  }
  ts.buttons.forEach(b => {
    const spec = buttonMaps.value[b.index];
    if (spec?.source === 'column' && spec.value) cols.push(spec.value);
  });
  return [...new Set(cols)];
});

const sourceRows = computed(() =>
  mode.value === 'csv' ? csvRows.value : manualRows.value
);

const validation = computed(() => {
  const valid = [];
  const errors = [];
  sourceRows.value.forEach(row => {
    const phone = normalizePhone(row.data.telefone);
    const cpf = normalizeCpf(row.data.cpf);
    const nome = String(row.data.nome || '').trim();
    let reason = '';
    if (!phone) reason = `telefone inválido "${row.data.telefone}"`;
    else if (!cpf) reason = `cpf inválido "${row.data.cpf}"`;
    else if (!nome) reason = 'nome vazio';
    else {
      const blankCol = mappedColumns.value.find(
        c => !String(row.data[c] || '').trim()
      );
      if (blankCol) reason = `coluna mapeada '${blankCol}' vazia`;
    }
    if (reason) {
      errors.push({ lineNo: row.lineNo, reason });
    } else {
      valid.push({
        row_number: row.lineNo,
        phone,
        cpf,
        name: nome,
        data: row.data,
      });
    }
  });
  return { valid, errors };
});

// --- previa da mensagem (Kairo 03/07/2026) -----------------------------------
// Mostra o template como VAI SAIR: variaveis substituidas com os dados do 1o
// cliente valido da lista (CSV) ou com os valores digitados, + foto/midia do
// cabecalho. Variavel ainda nao mapeada/preenchida continua como {{n}}.
const previewRow = computed(
  () => validation.value.valid[0] || sourceRows.value[0] || null
);

const resolvePreviewValue = spec => {
  if (!spec) return null;
  if (mode.value === 'manual' || spec.source === 'fixed') {
    return String(spec.value || '').trim() || null;
  }
  if (!previewRow.value || !spec.value) return null;
  return String(previewRow.value.data[spec.value] || '').trim() || null;
};

const applyVar = (text, name, value) =>
  value
    ? text.replace(new RegExp(`{{\\s*${name}\\s*}}`, 'g'), () => value)
    : text;

const previewBody = computed(() => {
  const ts = templateStructure.value;
  if (!ts) return '';
  let text = ts.bodyText;
  ts.bodyVars.forEach(v => {
    text = applyVar(text, v, resolvePreviewValue(bodyMap.value[v]));
  });
  return text;
});

const previewHeaderText = computed(() => {
  const ts = templateStructure.value;
  if (!ts || !ts.headerText || ts.headerMediaType) return '';
  let text = ts.headerText;
  if (ts.headerVar) text = applyVar(text, '1', resolvePreviewValue(headerMap.value));
  return text;
});

const previewMediaUrl = computed(() => {
  const ts = templateStructure.value;
  if (!ts?.headerMediaType) return '';
  const url = mediaUrl.value.trim();
  return /^https?:\/\//.test(url) ? url : '';
});

const previewSourceLabel = computed(() => {
  const ts = templateStructure.value;
  if (!ts) return '';
  const hasVars =
    ts.bodyVars.length || ts.headerVar || ts.headerMediaType || ts.buttons.length;
  if (!hasVars) return '';
  if (mode.value === 'manual') return 'Prévia com os valores fixos digitados.';
  if (previewRow.value) {
    const who =
      previewRow.value.data.nome || previewRow.value.data.telefone || '1º da lista';
    const line = previewRow.value.lineNo ?? previewRow.value.row_number;
    return `Prévia com os dados da linha ${line} do CSV (${who}).`;
  }
  return 'Suba o CSV para ver a prévia preenchida com os dados do 1º cliente.';
});

// --- pode disparar? ----------------------------------------------------------
const specFilled = spec => spec && String(spec.value || '').trim().length > 0;

const mappingComplete = computed(() => {
  const ts = templateStructure.value;
  if (!ts) return false;
  if (!ts.bodyVars.every(v => specFilled(bodyMap.value[v]))) return false;
  if (ts.headerVar && !specFilled(headerMap.value)) return false;
  if (ts.headerMediaType && !/^https?:\/\//.test(mediaUrl.value.trim()))
    return false;
  if (!ts.buttons.every(b => specFilled(buttonMaps.value[b.index])))
    return false;
  return true;
});

const canLaunch = computed(
  () =>
    !isLaunching.value &&
    campaignName.value.trim().length > 0 &&
    selectedTemplate.value &&
    mappingComplete.value &&
    validation.value.valid.length > 0 &&
    effectiveBoxes.value.length > 0
);

const normSpec = spec => ({
  source: mode.value === 'manual' ? 'fixed' : spec.source,
  value: String(spec.value || '').trim(),
});

const buildMapping = () => {
  const ts = templateStructure.value;
  const mapping = { body: {} };
  ts.bodyVars.forEach(v => {
    mapping.body[v] = normSpec(bodyMap.value[v]);
  });
  if (ts.headerVar) mapping.header_text = normSpec(headerMap.value);
  if (ts.headerMediaType) {
    mapping.header_media = {
      media_url: mediaUrl.value.trim(),
      media_type: ts.headerMediaType,
    };
  }
  if (ts.buttons.length) {
    mapping.buttons = ts.buttons.map(b => ({
      index: b.index,
      sub_type: b.sub_type,
      ...normSpec(buttonMaps.value[b.index]),
    }));
  }
  return mapping;
};

const resetForm = () => {
  campaignName.value = '';
  manualText.value = '';
  csvHeaders.value = [];
  csvRows.value = [];
  csvFileName.value = '';
  csvError.value = '';
  resetMappings();
};

const launch = async () => {
  if (!canLaunch.value) return;
  const boxes = effectiveBoxes.value;
  const total = validation.value.valid.length;
  const skipped = validation.value.errors.length;
  const isGroup =
    isMulti.value && timeMode.value === 'grupo' && boxes.length > 1;
  const parts = [
    `Disparar "${selectedTemplate.value.name}" para ${total} número(s)`,
  ];
  if (isMulti.value) {
    parts.push(
      `dividido(s) entre ${boxes.length} caixa(s): ${boxes.map(b => b.name).join(', ')}`
    );
  }
  parts.push(`intervalo: ${intervalLabel.value}`);
  if (isMulti.value && boxes.length > 1) {
    parts.push(
      isGroup
        ? 'tempo POR GRUPO (rodízio: 1 envio por intervalo no total)'
        : 'tempo POR NÚMERO (caixas em paralelo)'
    );
  }
  const confirmMsg = `${parts.join(' — ')}?${
    skipped ? ` (${skipped} linha(s) com erro ficam de fora)` : ''
  }`;
  // eslint-disable-next-line no-alert
  if (!window.confirm(confirmMsg)) return;
  isLaunching.value = true;
  launchError.value = '';
  try {
    const t = selectedTemplate.value;
    const groupToken = isGroup
      ? `grp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
      : '';
    // Lista ÚNICA dividida em rodízio entre as caixas (cliente recebe de UMA
    // caixa só); row_number preserva a linha original do CSV no relatório.
    const items = validation.value.valid;
    const perBox = boxes.map(() => []);
    items.forEach((it, i) => perBox[i % boxes.length].push(it));

    const created = [];
    for (const [bi, box] of boxes.entries()) {
      const slice = perBox[bi];
      if (!slice.length) continue; // mais caixas que linhas
      // eslint-disable-next-line no-await-in-loop
      const { data: campaign } = await InboxDispatchesAPI.createCampaign(
        box.id,
        {
          name: campaignName.value.trim(),
          template_name: t.name,
          template_language: t.language,
          template_category: t.category,
          template_data: { body_text: templateStructure.value.bodyText },
          variable_mapping: buildMapping(),
          interval_seconds: intervalSeconds.value,
          group_token: groupToken,
        }
      );
      for (let i = 0; i < slice.length; i += ITEM_BATCH_SIZE) {
        // envia em lotes sequenciais pra nao estourar o payload
        // eslint-disable-next-line no-await-in-loop
        await InboxDispatchesAPI.addItems(
          box.id,
          campaign.id,
          slice.slice(i, i + ITEM_BATCH_SIZE)
        );
      }
      created.push({ box, campaign });
    }
    if (isGroup) {
      await InboxDispatchesAPI.startGroupDispatch(groupToken);
    } else {
      for (const { box, campaign } of created) {
        // eslint-disable-next-line no-await-in-loop
        await InboxDispatchesAPI.startDispatch(box.id, campaign.id);
      }
    }
    useAlert(
      `Disparo "${campaignName.value.trim()}" iniciado (${total} números em ${created.length} caixa(s)).`
    );
    resetForm();
    await fetchCampaigns();
  } catch (e) {
    launchError.value = e?.response?.data?.error || e.message;
  } finally {
    isLaunching.value = false;
  }
};

// --- relatorio ----------------------------------------------------------------
const campaigns = ref([]);
const expandedId = ref(null);
let pollTimer = null;

const STATUS_LABELS = {
  queued: 'Na fila',
  running: 'Disparando',
  paused: 'Pausada',
  completed: 'Concluída',
};

const STATUS_CLASSES = {
  queued: 'bg-n-slate-3 text-n-slate-12',
  running: 'bg-n-amber-3 text-n-amber-12',
  paused: 'bg-n-ruby-3 text-n-ruby-12',
  completed: 'bg-n-teal-3 text-n-teal-12',
};

const fetchCampaigns = async () => {
  try {
    const lists = await Promise.all(
      targets.value.map(async box => {
        try {
          const { data } = await InboxDispatchesAPI.listCampaigns(box.id);
          return (data.data || []).map(c => ({
            ...c,
            _inboxId: box.id,
            _inboxName: box.name,
          }));
        } catch (e) {
          return [];
        }
      })
    );
    campaigns.value = lists
      .flat()
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  } catch (e) {
    // silencioso no polling; erro aparece nas acoes
  }
};

const toggleExpand = campaign => {
  expandedId.value = expandedId.value === campaign.id ? null : campaign.id;
};

const resume = async campaign => {
  try {
    if (campaign.group_token) {
      // campanha de lote em GRUPO: retomar religa o rodízio do grupo inteiro
      await InboxDispatchesAPI.startGroupDispatch(campaign.group_token);
      useAlert('Grupo retomado — linhas com erro serão puladas.');
    } else {
      await InboxDispatchesAPI.startDispatch(campaign._inboxId, campaign.id);
      useAlert('Disparo retomado — linhas com erro serão puladas.');
    }
    await fetchCampaigns();
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message);
  }
};

const pause = async campaign => {
  try {
    await InboxDispatchesAPI.pauseDispatch(campaign._inboxId, campaign.id);
    useAlert('Disparo pausado.');
    await fetchCampaigns();
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message);
  }
};

// --- CANCELAR a campanha (Kairo 28/07/2026) --------------------------------
// Pausar é reversível (CONTINUAR religa, e o lote em grupo religa junto);
// cancelar encerra de vez. Por ser irreversível, exige confirmação e diz
// quantos números ficarão sem receber.
const cancelando = ref(null);

const cancelar = async campaign => {
  const faltam = campaign.counters?.pendentes ?? 0;
  const ok = window.confirm(
    `Cancelar a campanha "${campaign.name}"?\n\n` +
      `${faltam} número(s) NÃO vão receber a mensagem. ` +
      'Isso não pode ser desfeito — para disparar de novo é preciso criar outra campanha.'
  );
  if (!ok) return;
  cancelando.value = campaign.id;
  try {
    const { data } = await InboxDispatchesAPI.cancelDispatch(
      campaign._inboxId,
      campaign.id
    );
    useAlert(`Campanha cancelada — ${data.nao_enviados} número(s) não receberão.`);
    await fetchCampaigns();
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message);
  } finally {
    cancelando.value = null;
  }
};

// --- TEMPO da campanha (Kairo 28/07/2026) ------------------------------------
// Muda o intervalo entre envios DEPOIS de criada, com a campanha até rodando:
// antes o min:seg do primeiro clique valia para o lote inteiro. O serviço relê
// o intervalo a cada mensagem, então não precisa pausar — o novo tempo vale a
// partir do próximo envio.
const tempoId = ref(null);
const tempoMin = ref(0);
const tempoSec = ref(0);
const tempoSalvando = ref(false);

const tempoTexto = segundos => {
  const total = Number(segundos || 0);
  if (!total) return 'mínimo do sistema';
  const m = Math.floor(total / 60);
  const s = total % 60;
  return [m ? `${m} min` : '', s ? `${s} seg` : ''].filter(Boolean).join(' e ');
};

const toggleTempo = campaign => {
  if (tempoId.value === campaign.id) {
    tempoId.value = null;
    return;
  }
  const total = Number(campaign.interval_seconds || 0);
  tempoMin.value = Math.floor(total / 60);
  tempoSec.value = total % 60;
  tempoId.value = campaign.id;
};

const salvarTempo = async campaign => {
  const segundos = Number(tempoMin.value || 0) * 60 + Number(tempoSec.value || 0);
  tempoSalvando.value = true;
  try {
    await InboxDispatchesAPI.updateInterval(
      campaign._inboxId,
      campaign.id,
      segundos
    );
    tempoId.value = null;
    useAlert(
      campaign.status === 'running'
        ? `Tempo alterado para ${tempoTexto(segundos)} — vale a partir do próximo envio.`
        : `Tempo alterado para ${tempoTexto(segundos)}.`
    );
    await fetchCampaigns();
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message);
  } finally {
    tempoSalvando.value = false;
  }
};

// O refresh dos numeros da campanha expandida vive no DispatchItemsReport.
const poll = () => {
  fetchCampaigns();
};

const formatDate = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

// Monta o token {{n}} em JS — chaves duplas cruas quebrariam o parser do Vue.
const varToken = v => `{{${v}}}`;

onMounted(() => {
  fetchTemplates();
  fetchCampaigns();
  pollTimer = setInterval(poll, 6000);
});

onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer);
});

const inputClass =
  'w-full h-10 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand';
const selectClass =
  'w-full h-10 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline-none !mb-0';
const textareaClass =
  'w-full rounded-lg border border-n-weak bg-n-alpha-black2 p-3 text-sm text-n-slate-12 outline-none focus:border-n-brand';
</script>

<template>
  <div class="mx-6 flex flex-col gap-8">
    <!-- ==================== NOVO DISPARO ==================== -->
    <div>
      <h2 class="text-heading-2 text-n-slate-12 mb-1">Novo disparo</h2>
      <p class="text-n-slate-11 text-sm mb-5">
        Escolha um template Aprovado, mapeie as variáveis e dispare para a
        lista importada (CSV) ou digitada. Cada envio cria/reusa o contato e a
        conversa no Chatwoot — o recebido/lido vem do webhook da Meta.
      </p>

      <div class="flex flex-col gap-4">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="text-sm font-medium text-n-slate-12 mb-1 block">
              Nome da campanha
            </label>
            <input
              v-model="campaignName"
              :class="inputClass"
              maxlength="120"
              placeholder="ex: promo_julho_lista_quente"
            />
          </div>
          <div>
            <label class="text-sm font-medium text-n-slate-12 mb-1 block">
              Template (só Aprovados)
            </label>
            <select v-if="isMulti" v-model="selectedKey" :class="selectClass">
              <option value="" disabled>
                {{ isLoadingTemplates ? 'Carregando…' : 'Selecione o template' }}
              </option>
              <option v-for="u in unionTemplates" :key="u.key" :value="u.key">
                {{ u.template.name }} ({{ u.template.language }}) — em
                {{ u.boxIds.length }} de {{ targets.length }} caixa(s)
              </option>
            </select>
            <select v-else v-model="selectedIdx" :class="selectClass">
              <option value="" disabled>
                {{ isLoadingTemplates ? 'Carregando…' : 'Selecione o template' }}
              </option>
              <option
                v-for="(t, idx) in approvedTemplates"
                :key="t.id"
                :value="String(idx)"
              >
                {{ t.name }} ({{ t.language }}) — {{ t.category }}
              </option>
            </select>
          </div>
        </div>

        <!-- Multi-caixa: quem TEM o template escolhido fica marcado (dispara);
             caixa sem o template de mesmo nome fica de fora do lote. -->
        <div
          v-if="isMulti && selectedUnion"
          class="flex flex-wrap gap-2 items-center"
        >
          <span
            v-for="box in targets"
            :key="box.id"
            class="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs border"
            :class="
              selectedUnion.boxIds.includes(box.id)
                ? 'border-n-teal-8 text-n-teal-11 bg-n-teal-3'
                : 'border-n-weak text-n-slate-10 line-through'
            "
          >
            {{ selectedUnion.boxIds.includes(box.id) ? '✓' : '✕' }}
            {{ box.name }}
            <span v-if="!selectedUnion.boxIds.includes(box.id)">
              (sem o template)
            </span>
          </span>
        </div>

        <template v-if="templateStructure">
          <div class="rounded-lg border border-n-weak p-4">
            <p class="text-xs font-medium text-n-slate-11 mb-2">
              Prévia da mensagem:
            </p>
            <div
              class="rounded-lg bg-n-alpha-black2 p-3 max-w-lg flex flex-col gap-2"
            >
              <img
                v-if="previewMediaUrl && templateStructure.headerMediaType === 'image'"
                :src="previewMediaUrl"
                class="rounded-lg max-h-48 w-auto object-contain self-start"
              />
              <video
                v-else-if="previewMediaUrl && templateStructure.headerMediaType === 'video'"
                :src="previewMediaUrl"
                controls
                class="rounded-lg max-h-48 self-start"
              />
              <p
                v-else-if="previewMediaUrl"
                class="text-xs text-n-slate-11 break-all"
              >
                📄 Documento: {{ previewMediaUrl }}
              </p>
              <p
                v-else-if="templateStructure.headerMediaType"
                class="text-xs text-n-slate-10"
              >
                (informe a URL da mídia do cabeçalho para ver a prévia)
              </p>
              <p
                v-if="previewHeaderText"
                class="text-sm font-semibold text-n-slate-12 whitespace-pre-wrap"
              >
                {{ previewHeaderText }}
              </p>
              <p class="text-sm text-n-slate-12 whitespace-pre-wrap">
                {{ previewBody || '(sem corpo de texto)' }}
              </p>
            </div>
            <p v-if="previewSourceLabel" class="text-xs text-n-slate-10 mt-2">
              {{ previewSourceLabel }}
            </p>
          </div>

          <!-- Origem dos dados -->
          <div class="flex gap-2">
            <NextButton
              size="sm"
              :variant="mode === 'csv' ? 'solid' : 'outline'"
              label="Importar CSV"
              @click="mode = 'csv'"
            />
            <NextButton
              size="sm"
              :variant="mode === 'manual' ? 'solid' : 'outline'"
              label="Digitar"
              @click="mode = 'manual'"
            />
          </div>

          <template v-if="mode === 'csv'">
            <div class="rounded-lg border border-n-weak p-4 flex flex-col gap-3">
              <div class="flex items-center justify-between flex-wrap gap-2">
                <label class="text-sm font-medium text-n-slate-12">
                  Lista (CSV com colunas obrigatórias: telefone, cpf, nome)
                </label>
                <NextButton
                  size="xs"
                  variant="outline"
                  label="Baixar modelo CSV"
                  @click="downloadCsvModel"
                />
              </div>
              <input
                type="file"
                accept=".csv,text/csv"
                class="text-sm text-n-slate-11"
                @change="onCsvSelected"
              />
              <p v-if="csvError" class="text-xs text-n-ruby-11">
                {{ csvError }}
              </p>
              <p v-else-if="csvFileName" class="text-xs text-n-teal-11">
                ✓ {{ csvFileName }} — {{ csvRows.length }} linha(s), colunas:
                {{ csvHeaders.join(', ') }}
              </p>
              <p class="text-xs text-n-slate-10">
                Toda coluna do cabeçalho vira uma variável disponível no
                mapeamento abaixo. Separador ; ou , (detectado sozinho).
              </p>
            </div>
          </template>

          <template v-else>
            <div class="rounded-lg border border-n-weak p-4 flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                Leads (um por linha: telefone;cpf;nome)
              </label>
              <textarea
                v-model="manualText"
                :class="textareaClass"
                rows="5"
                placeholder="5521999999999;12345678901;Maria da Silva"
              />
              <p class="text-xs text-n-slate-10">
                No modo digitado as variáveis do template recebem os valores
                fixos abaixo (iguais para todos os números).
              </p>
            </div>
          </template>

          <!-- Mapeamento das variaveis -->
          <div
            v-if="
              templateStructure.bodyVars.length ||
              templateStructure.headerVar ||
              templateStructure.headerMediaType ||
              templateStructure.buttons.length
            "
            class="rounded-lg border border-n-weak p-4 flex flex-col gap-3"
          >
            <label class="text-sm font-medium text-n-slate-12">
              Variáveis do template
            </label>

            <div
              v-for="v in templateStructure.bodyVars"
              :key="`body-${v}`"
              class="grid grid-cols-3 gap-2 items-center"
            >
              <span class="text-sm font-mono text-n-slate-11">
                Corpo {{ varToken(v) }}
              </span>
              <select
                v-if="mode === 'csv'"
                v-model="bodyMap[v].source"
                :class="selectClass"
              >
                <option value="column">Coluna do CSV</option>
                <option value="fixed">Texto fixo</option>
              </select>
              <span v-else class="text-xs text-n-slate-10">valor fixo</span>
              <select
                v-if="mode === 'csv' && bodyMap[v].source === 'column'"
                v-model="bodyMap[v].value"
                :class="selectClass"
              >
                <option value="" disabled>Escolha a coluna</option>
                <option v-for="h in csvHeaders" :key="h" :value="h">
                  {{ h }}
                </option>
              </select>
              <input
                v-else
                v-model="bodyMap[v].value"
                :class="inputClass"
                placeholder="Valor da variável"
              />
            </div>

            <div
              v-if="templateStructure.headerVar"
              class="grid grid-cols-3 gap-2 items-center"
            >
              <span class="text-sm font-mono text-n-slate-11">
                Cabeçalho {{ varToken(1) }}
              </span>
              <select
                v-if="mode === 'csv'"
                v-model="headerMap.source"
                :class="selectClass"
              >
                <option value="column">Coluna do CSV</option>
                <option value="fixed">Texto fixo</option>
              </select>
              <span v-else class="text-xs text-n-slate-10">valor fixo</span>
              <select
                v-if="mode === 'csv' && headerMap.source === 'column'"
                v-model="headerMap.value"
                :class="selectClass"
              >
                <option value="" disabled>Escolha a coluna</option>
                <option v-for="h in csvHeaders" :key="h" :value="h">
                  {{ h }}
                </option>
              </select>
              <input
                v-else
                v-model="headerMap.value"
                :class="inputClass"
                placeholder="Valor do cabeçalho"
              />
            </div>

            <div
              v-if="templateStructure.headerMediaType"
              class="grid grid-cols-3 gap-2 items-center"
            >
              <span class="text-sm text-n-slate-11">
                Mídia do cabeçalho ({{ templateStructure.headerMediaType }})
              </span>
              <span class="text-xs text-n-slate-10">URL pública (https)</span>
              <input
                v-model="mediaUrl"
                :class="inputClass"
                placeholder="https://exemplo.com/banner.jpg"
              />
            </div>

            <div
              v-for="b in templateStructure.buttons"
              :key="`btn-${b.index}`"
              class="grid grid-cols-3 gap-2 items-center"
            >
              <span class="text-sm text-n-slate-11">Botão {{ b.label }}</span>
              <select
                v-if="mode === 'csv'"
                v-model="buttonMaps[b.index].source"
                :class="selectClass"
              >
                <option value="column">Coluna do CSV</option>
                <option value="fixed">Texto fixo</option>
              </select>
              <span v-else class="text-xs text-n-slate-10">valor fixo</span>
              <select
                v-if="mode === 'csv' && buttonMaps[b.index].source === 'column'"
                v-model="buttonMaps[b.index].value"
                :class="selectClass"
              >
                <option value="" disabled>Escolha a coluna</option>
                <option v-for="h in csvHeaders" :key="h" :value="h">
                  {{ h }}
                </option>
              </select>
              <input
                v-else
                v-model="buttonMaps[b.index].value"
                :class="inputClass"
                placeholder="Valor do parâmetro"
              />
            </div>
          </div>

          <!-- Preview / validacao -->
          <div
            v-if="sourceRows.length"
            class="rounded-lg border border-n-weak p-4 flex flex-col gap-2"
          >
            <p class="text-sm text-n-slate-12">
              <span class="font-medium text-n-teal-11">
                {{ validation.valid.length }} linha(s) prontas
              </span>
              <span v-if="validation.errors.length" class="text-n-ruby-11">
                · {{ validation.errors.length }} com erro (ficam de fora)
              </span>
            </p>
            <div
              v-if="validation.errors.length"
              class="max-h-32 overflow-y-auto flex flex-col gap-1"
            >
              <p
                v-for="err in validation.errors.slice(0, 50)"
                :key="err.lineNo"
                class="text-xs text-n-ruby-11"
              >
                Linha {{ err.lineNo }}: {{ err.reason }}
              </p>
              <p
                v-if="validation.errors.length > 50"
                class="text-xs text-n-slate-10"
              >
                … e mais {{ validation.errors.length - 50 }} erro(s).
              </p>
            </div>
          </div>

          <!-- Tempo de disparo (Kairo 10/07/2026): intervalo min:seg entre os
               envios + modo do tempo quando há mais de uma caixa no lote. -->
          <div class="rounded-lg border border-n-weak p-4 flex flex-col gap-3">
            <label class="text-sm font-medium text-n-slate-12">
              Tempo de disparo
            </label>
            <div class="flex items-end gap-3 flex-wrap">
              <div>
                <span class="text-xs text-n-slate-11 block mb-1">Minutos</span>
                <select v-model.number="intervalMin" :class="selectClass">
                  <option v-for="m in 60" :key="`m-${m - 1}`" :value="m - 1">
                    {{ String(m - 1).padStart(2, '0') }}
                  </option>
                </select>
              </div>
              <div>
                <span class="text-xs text-n-slate-11 block mb-1">Segundos</span>
                <select v-model.number="intervalSec" :class="selectClass">
                  <option v-for="s in 60" :key="`s-${s - 1}`" :value="s - 1">
                    {{ String(s - 1).padStart(2, '0') }}
                  </option>
                </select>
              </div>
              <p class="text-xs text-n-slate-10 pb-2.5">
                Intervalo entre envios: {{ intervalLabel }}.
                <span v-if="intervalSeconds === 0">
                  (00:00 = velocidade máxima do sistema)
                </span>
              </p>
            </div>
            <div v-if="isMulti && effectiveBoxes.length > 1" class="flex flex-col gap-1.5">
              <label class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer">
                <input v-model="timeMode" type="radio" value="numero" />
                <span>
                  <b>Tempo por número</b> — cada caixa dispara em paralelo,
                  respeitando o intervalo entre os envios DELA.
                </span>
              </label>
              <label class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer">
                <input v-model="timeMode" type="radio" value="grupo" />
                <span>
                  <b>Tempo por grupo</b> — rodízio: dispara de uma caixa,
                  espera o intervalo, dispara da próxima (1 envio por intervalo
                  no total).
                </span>
              </label>
            </div>
          </div>

          <p v-if="launchError" class="text-sm text-n-ruby-11 whitespace-pre-wrap">
            {{ launchError }}
          </p>

          <div class="flex justify-end">
            <NextButton
              :label="isMulti ? 'DISPARAR EM LOTE' : 'Disparar'"
              :is-loading="isLaunching"
              :disabled="!canLaunch"
              @click="launch"
            />
          </div>
        </template>
      </div>
    </div>

    <!-- ==================== RELATORIO ==================== -->
    <div>
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-heading-2 text-n-slate-12">Relatório de disparos</h2>
        <NextButton
          size="xs"
          variant="outline"
          color="slate"
          label="Atualizar"
          @click="fetchCampaigns"
        />
      </div>

      <div
        v-if="!campaigns.length"
        class="text-sm text-n-slate-11 py-6 text-center border border-n-weak rounded-lg"
      >
        Nenhum disparo realizado nesta caixa ainda.
      </div>

      <div v-else class="overflow-x-auto rounded-lg border border-n-weak">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-n-weak text-left text-n-slate-11">
              <th class="px-3 py-2 font-medium">Campanha</th>
              <th v-if="isMulti" class="px-3 py-2 font-medium">Caixa</th>
              <th class="px-3 py-2 font-medium">Template</th>
              <th class="px-3 py-2 font-medium">Criada em</th>
              <th class="px-3 py-2 font-medium">Status</th>
              <th class="px-3 py-2 font-medium">Total</th>
              <th class="px-3 py-2 font-medium">Disparados</th>
              <th class="px-3 py-2 font-medium">Entregues</th>
              <th class="px-3 py-2 font-medium">Lidos</th>
              <th class="px-3 py-2 font-medium">Erros</th>
              <th class="px-3 py-2 font-medium">Pendentes</th>
              <th class="px-3 py-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            <template v-for="c in campaigns" :key="c.id">
              <tr
                class="border-b border-n-weak last:border-b-0 cursor-pointer hover:bg-n-alpha-black1"
                @click="toggleExpand(c)"
              >
                <td class="px-3 py-2 font-medium text-n-slate-12">
                  {{ c.name }}
                  <span
                    v-if="c.group_token"
                    class="ml-1 text-[10px] text-n-slate-10"
                    title="Disparo em grupo (rodízio entre caixas)"
                  >
                    ⛓ grupo
                  </span>
                </td>
                <td v-if="isMulti" class="px-3 py-2 text-xs text-n-slate-11">
                  {{ c._inboxName }}
                </td>
                <td class="px-3 py-2 font-mono text-xs text-n-slate-11">
                  {{ c.template_name }}
                </td>
                <td class="px-3 py-2 text-n-slate-11">
                  {{ formatDate(c.created_at) }}
                </td>
                <td class="px-3 py-2">
                  <span
                    class="inline-flex px-2 py-0.5 rounded text-xs font-medium"
                    :class="STATUS_CLASSES[c.status]"
                  >
                    {{ STATUS_LABELS[c.status] || c.status }}
                  </span>
                </td>
                <td class="px-3 py-2 text-n-slate-12">{{ c.counters.total }}</td>
                <td class="px-3 py-2 text-n-teal-11">
                  {{ c.counters.disparados }}
                </td>
                <td class="px-3 py-2 text-n-teal-11">
                  {{ c.counters.entregues }}
                </td>
                <td class="px-3 py-2 text-n-blue-11">{{ c.counters.lidos }}</td>
                <td class="px-3 py-2 text-n-ruby-11">{{ c.counters.erros }}</td>
                <td class="px-3 py-2 text-n-slate-11">
                  {{ c.counters.pendentes }}
                </td>
                <td class="px-3 py-2 text-right whitespace-nowrap" @click.stop>
                  <!-- TEMPO: abre o intervalo atual para editar sem parar o
                       disparo. Só some quando a campanha já acabou. -->
                  <button
                    v-if="c.status !== 'completed'"
                    type="button"
                    class="mr-2 px-2 py-1 rounded text-xs font-medium bg-n-blue-3 text-n-blue-11 border border-n-blue-6 hover:bg-n-blue-4"
                    :title="`Intervalo entre envios: ${tempoTexto(c.interval_seconds)}`"
                    @click="toggleTempo(c)"
                  >
                    TEMPO
                  </button>
                  <NextButton
                    v-if="c.status === 'paused' || c.stalled"
                    size="xs"
                    label="CONTINUAR"
                    @click="resume(c)"
                  />
                  <NextButton
                    v-else-if="['queued', 'running'].includes(c.status)"
                    size="xs"
                    variant="outline"
                    color="ruby"
                    label="Pausar"
                    @click="pause(c)"
                  />
                  <!-- CANCELAR: encerra de vez. Só enquanto houver o que enviar. -->
                  <NextButton
                    v-if="c.status !== 'completed' && (c.counters.pendentes || 0) > 0"
                    size="xs"
                    color="ruby"
                    label="CANCELAR"
                    class="ml-2"
                    :is-loading="cancelando === c.id"
                    @click="cancelar(c)"
                  />
                </td>
              </tr>
              <tr v-if="tempoId === c.id" class="border-b border-n-weak">
                <td
                  :colspan="isMulti ? 12 : 11"
                  class="px-3 py-3 bg-n-alpha-black1"
                  @click.stop
                >
                  <div class="flex items-end gap-3 flex-wrap">
                    <div>
                      <span class="text-xs text-n-slate-11 block mb-1">
                        Minutos
                      </span>
                      <select v-model.number="tempoMin" :class="selectClass">
                        <option
                          v-for="m in 60"
                          :key="`tm-${m - 1}`"
                          :value="m - 1"
                        >
                          {{ String(m - 1).padStart(2, '0') }}
                        </option>
                      </select>
                    </div>
                    <div>
                      <span class="text-xs text-n-slate-11 block mb-1">
                        Segundos
                      </span>
                      <select v-model.number="tempoSec" :class="selectClass">
                        <option
                          v-for="s in 60"
                          :key="`ts-${s - 1}`"
                          :value="s - 1"
                        >
                          {{ String(s - 1).padStart(2, '0') }}
                        </option>
                      </select>
                    </div>
                    <NextButton
                      size="xs"
                      label="Salvar tempo"
                      :is-loading="tempoSalvando"
                      class="mb-0.5"
                      @click="salvarTempo(c)"
                    />
                    <NextButton
                      size="xs"
                      variant="outline"
                      label="Cancelar"
                      class="mb-0.5"
                      @click="tempoId = null"
                    />
                    <p class="text-xs text-n-slate-10 pb-1.5">
                      Tempo atual: {{ tempoTexto(c.interval_seconds) }}.
                      <span v-if="c.status === 'running'">
                        Vale a partir do próximo envio — não precisa pausar.
                      </span>
                      <span v-if="c.group_token">
                        Campanha em grupo: o tempo vale para o rodízio inteiro.
                      </span>
                    </p>
                  </div>
                </td>
              </tr>
              <tr v-if="c.pause_reason && c.status === 'paused'">
                <td :colspan="isMulti ? 12 : 11" class="px-3 py-1 text-xs text-n-ruby-11">
                  {{ c.pause_reason }}
                </td>
              </tr>
              <tr v-if="expandedId === c.id" class="border-b border-n-weak">
                <td :colspan="isMulti ? 12 : 11" class="px-3 py-3 bg-n-alpha-black1">
                  <DispatchItemsReport :inbox-id="c._inboxId" :campaign="c" />
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
